<template>
  <section v-editable="blok" hero-bold :style="[bgStyle]">
    <div class="container container--bleed">
      <div class="section-row">
        <div class="section-content">
          <h1 v-if="blok.title" class="section-title" v-html="blok.title"></h1>
          <div v-if="blok.description" class="section-description" v-html="blok.description"></div>
          <div v-if="blok.buttons" class="section-buttons">
            <StoryblokComponent v-for="button in blok.buttons" :key="button._uid" :blok="button" />
          </div>
        </div>

        <div v-if="blok?.lottie && blok.lottie?.length > 0" class="section-image">
          <canvas ref="canvas" class="lottie-wrapper" width="100%" height="100%" />
        </div>
        <div v-else class="section-image">
          <img v-if="blok.image?.filename" :src="optimizeImage(blok.image.filename)" alt="Hero Banner Image" loading="lazy" />

          <div v-if="blok?.video?.length > 0" class="section-video">
            <video loop muted playsInline class="w-auto h-full" autoplay :poster="optimizeImage(blok.image.filename)">
              <source v-for="(source, index) in videoSources" :key="index" :src="source.url" :type="source.type" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

      <div v-if="blok.partners" class="section-partners">
        <div class="section-partners-label">Trusted by over 35,000 businesses globally</div>
        <div class="section-partners-row">
          <NuxtImg v-for="(src, s) in blok.partners" :key="s" provider="storyblok" :src="src.filename" :alt="`Partner ${src.filename.replace('/img/', '').replace('-logo.svg', '')}`" width="90" height="24" />
        </div>
      </div>
      </div>
    </div>
  </section>
</template>
<script setup>
  import { DotLottie, DotLottieWorker } from "@lottiefiles/dotlottie-web";
  const props = defineProps({ blok: Object });

  const canvas = ref(null);

  useHead({
    link: [
      props.blok.background ? {
        rel: "preload",
        as: "image",
        fetchpriority: "high",
        href: optimizeImage(props.blok.background?.filename),
      }: null,
      props.blok?.video ? {
        rel: "preload",
        as: "video",
        href: props.blok?.video,
        type: "video/webm",
      } : null,
    ],
  });

  const bgStyle = computed(() => {
    if (!props.blok.background?.filename) return {};
    return {
      backgroundImage: `url(${props.blok.background?.filename})`,
      backgroundColor: props.blok?.background_color?.color || "#FFF",
    };
  });

  const getVideoSources = (videoString) => {
    if (!videoString) return [];
    return videoString.split(",").map((url) => {
      const extension = url.split(".").pop();
      let type = "";
      switch (extension) {
        case "webm":
          type = "video/webm";
          break;
        case "mov":
          type = "video/quicktime";
          break;
        default:
          type = "";
      }
      return { url, type };
    });
  };

  const videoSources = computed(() => getVideoSources(props.blok?.video));

  onMounted(() => {
    if (import.meta.client) {
      if (canvas.value) {
        let dotLottie;

        let totalFrames = 0;

        try {
          // Use DotLottieWorker if supported
          dotLottie = new DotLottieWorker({
            canvas: canvas.value,
            src: props.blok.lottie,
            autoplay: false,
          });
        } catch (e) {
          console.log(e);
          // Use original DotLottie if worker is not supported
          dotLottie = new DotLottie({
            canvas: canvas.value,
            src: props.blok.lottie,
            autoplay: false,
          });
        }

        dotLottie.addEventListener("load", () => {
          dotLottie.setLayout({ fit: "fit-height", align: [0.5, 0.5] });
          dotLottie.setFrame(59);
          dotLottie.play();

          totalFrames = Math.floor(dotLottie.totalFrames);
        });

        dotLottie.addEventListener("stop", () => {
          dotLottie.setFrame(59);
          dotLottie.play();
        });

        dotLottie.addEventListener("frame", (e) => {
          if (e.currentFrame > 0 && totalFrames > 0) {
            if (e.currentFrame >= totalFrames) {
              dotLottie.stop();
            }
          }
        });
      }
    }
  });
</script>
<style src="~/assets/scss/section/hero-bold.scss" lang="scss"></style>
